/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Locates and parses the C2PA `uuid` box embedded in a BMFF asset.
 *
 * Per the C2PA spec (§ "BMFF-based hashes"), C2PA data lives in a top-level box
 * of type `uuid` with the C2PA usertype. Its payload is:
 *
 *   uint32 version_flags (= 0)
 *   string purpose        (null-terminated UTF-8: "manifest" | "merkle")
 *   if purpose == "manifest":
 *       uint64 merkle_offset
 *       <JUMBF manifest store bytes…>
 *   if purpose == "merkle":
 *       <CBOR map: { uniqueId, localId, location, hashes:[…] }>
 */

import { scanBoxes, usertypeEquals, type Box } from './parseBoxes'

/** The C2PA box usertype: `d8fe c3d6 1b0e 483c 9297 5828 877e c481`. */
export const C2PA_UUID = new Uint8Array([
    0xd8, 0xfe, 0xc3, 0xd6, 0x1b, 0x0e, 0x48, 0x3c,
    0x92, 0x97, 0x58, 0x28, 0x87, 0x7e, 0xc4, 0x81
])

/** The manifest store carried by an init segment's C2PA box. */
export interface C2paManifestBox {
    /**
     * `manifest` is the common case; `original`/`update` are the C2PA composed-asset /
     * update-manifest flows (§A.5) and carry the same [uint64 merkle offset][JUMBF] body.
     */
    purpose: 'manifest' | 'original' | 'update'
    merkleOffset: number
    /** Raw JUMBF manifest-store bytes. */
    jumbf: Uint8Array
}

/** The per-fragment Merkle box carried by a media segment's C2PA box. */
export interface C2paMerkleBox {
    purpose: 'merkle'
    /** Raw CBOR bytes of the merkle map (decode with the cbor module). */
    cbor: Uint8Array
}

export type C2paBoxContent = C2paManifestBox | C2paMerkleBox

/** Finds the first top-level C2PA `uuid` box in `u8`, or `null` if absent. */
export function findC2paBox (u8: Uint8Array): Box | null {
    for (const box of scanBoxes(u8)) {
        if (box.type === 'uuid' && usertypeEquals(box, C2PA_UUID)) {
            return box
        }
    }
    return null
}

/**
 * Finds ALL top-level C2PA `uuid` boxes in `u8`, in file order. A multi-track
 * media fragment carries one Merkle `uuid` box per track (BMFF-1); every one must
 * be located and verified, not just the first.
 */
export function findAllC2paBoxes (u8: Uint8Array): Box[] {
    const out: Box[] = []
    for (const box of scanBoxes(u8)) {
        if (box.type === 'uuid' && usertypeEquals(box, C2PA_UUID)) out.push(box)
    }
    return out
}

/**
 * Reads a null-terminated ASCII string at `off` within `u8`, bounded by `end`.
 * Returns the string and the offset just past the terminating NUL.
 */
const c2paUtf8 = typeof TextDecoder !== 'undefined' ? new TextDecoder() : null
function readCString (u8: Uint8Array, off: number, end: number): { value: string, next: number } {
    let i = off
    while (i < end && u8[i] !== 0) i++
    // Decode without spreading the span into String.fromCharCode arguments — a very
    // long (or unterminated) purpose string would otherwise exceed the call-arg limit.
    const span = u8.subarray(off, i)
    const value = c2paUtf8 ? c2paUtf8.decode(span) : Array.from(span, b => String.fromCharCode(b)).join('')
    return { value, next: i + 1 } // skip the NUL
}

/**
 * Parses the C2PA box content (manifest store or merkle map) from a located
 * C2PA `uuid` box. Returns `null` if the purpose is unrecognized.
 */
export function parseC2paBox (u8: Uint8Array, box: Box): C2paBoxContent | null {
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength)
    // payload = [16-byte usertype][uint32 version_flags][purpose\0]…
    let off = box.payloadStart + 16
    off += 4 // version_flags
    const { value: purpose, next } = readCString(u8, off, box.end)
    off = next

    // `manifest`, `original`, and `update` all carry [uint64 merkle offset][JUMBF].
    if (purpose === 'manifest' || purpose === 'original' || purpose === 'update') {
        const hi = dv.getUint32(off, false)
        const lo = dv.getUint32(off + 4, false)
        const merkleOffset = hi * 2 ** 32 + lo
        off += 8
        return { purpose, merkleOffset, jumbf: u8.subarray(off, box.end) }
    }
    if (purpose === 'merkle') {
        return { purpose: 'merkle', cbor: u8.subarray(off, box.end) }
    }
    return null
}

/** Convenience: locate + parse the C2PA box in one call. */
export function readC2paBox (u8: Uint8Array): C2paBoxContent | null {
    const box = findC2paBox(u8)
    return box ? parseC2paBox(u8, box) : null
}
